import { useAuth } from "../hooks/useAuth";
import { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";

const API = import.meta.env.VITE_API_URL || "https://api.runwaycrew.com";

const SHOW_SUGGESTED = false; 

/** Inline, self-contained login form */
function LoginForm({ redirectTo = "/dashboard" }: { redirectTo?: string }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setMsg(null);
    setBusy(true);
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, password }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.error || "Invalid email or password");
      }
      window.location.assign(redirectTo || "/dashboard");
    } catch (err: any) {
      setMsg(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={onSubmit} className="bg-white p-8 rounded-xl border shadow-lg w-full max-w-md space-y-4">
      <h2 className="text-2xl font-bold text-center">Welcome Back</h2>
      {msg && <div className="text-red-600 text-sm bg-red-50 p-3 rounded">{msg}</div>}
      <div>
        <label className="block text-sm font-medium mb-2">Email</label>
        <input
          type="email"
          className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 focus:border-blue-500 focus:outline-none transition-colors"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
      </div>
      <div>
        <label className="block text-sm font-medium mb-2">Password</label>
        <input
          type="password"
          className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 focus:border-blue-500 focus:outline-none transition-colors"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
      </div>
      <button
        className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-60 transition-colors"
        disabled={busy}
      >
        {busy ? "Signing in…" : "Sign in"}
      </button>
    </form>
  );
}

type DueTask = { id: number; title: string; status?: string | null };

type ScoreResp = {
  week_start_date?: string | null;
  runway_score: number;
  breakdown?: Record<string, number>;
  // NEW fields from v_runway_score_with_tasks
  tasks_completed_count?: number;
  tasks_overdue_open_count?: number;
  tasks_new_count?: number;
  tasks_summary?: string;
  due_task_titles?: DueTask[] | null;
} | null;

type NorthStarMetric =
  | "revenue"
  | "active_subs"
  | "orders"
  | "customer_ltv";

type NorthStar = {
  id: string;
  brand_id: string;
  goal: string;
  target_value?: number | null;
  metric_type?: NorthStarMetric | null; // strongly typed now
  deadline?: string | null;
  status: "active" | "completed" | "abandoned" | "archived";
  completed_at?: string | null;
  created_at: string;
  updated_at?: string;
  notes?: string | null;
};



type Task = {
  id: string;
  title: string;
  description?: string | null;
  status: "open" | "in_progress" | "completed" | "requested" | string;
  kpi?: string | null;
  due_date?: string | null;
  created_at?: string | null;
  // new/optional fields for brand-created tasks
  priority?: "low" | "normal" | "high" | "urgent";
  requested_due_date?: string | null;
  approved?: boolean | null;
  created_by_role?: "admin" | "brand";
};

type KpiRow = {
  kpi_group: "orders" | "subscriptions" | string;
  kpi_label: string;
  current_value: number | null;
  target_value: number | null;
  gap_value: number | null;
  status: "OK" | "Watch" | "Action" | "No target" | string;
  suggested_lever: string | null;
};

function NorthStarCard({ northStar, currentMetrics }: { northStar: NorthStar | null; currentMetrics: any }) {
  if (!northStar) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 h-full">
        <h2 className="text-sm font-semibold text-gray-700 mb-4">North Star Goal</h2>
        <div className="flex flex-col items-center justify-center py-12 text-center text-gray-400">
          <div className="text-4xl mb-3">🎯</div>
          <div className="text-sm">No active goal set</div>
        </div>
      </div>
    );
  }

  // Calculate progress + current + gap
  const currentValue = getCurrentValue(northStar, currentMetrics);
  const progress = calculateProgress(northStar, currentMetrics);
  const daysRemaining = northStar.deadline ? getDaysRemaining(northStar.deadline) : null;
  const progressColor = getProgressColor(progress, daysRemaining);

  const rawTarget =
  northStar.target_value != null ? Number(northStar.target_value) : null;

  const hasTarget =
  rawTarget !== null && !Number.isNaN(rawTarget);

  const rawGap = hasTarget ? rawTarget - currentValue : null;

  const gap = rawGap != null ? Math.max(0, rawGap) : null;

  const achieved = hasTarget && rawGap !== null && rawGap <= 0;

  const showTimeRemaining =
  daysRemaining !== null &&
  !achieved &&
  northStar.status !== "completed" &&
  northStar.status !== "archived";

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 h-full">
      <h2 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
        🎯 North Star Goal
      </h2>

      {/* Goal Statement */}
      <div className="mb-6">
        <div className="text-lg font-bold text-gray-900 leading-tight">
          {northStar.goal}
        </div>
        {northStar.deadline && (
          <div className="text-xs text-gray-500 mt-1">
            Target: {formatNSDate(northStar.deadline)}
          </div>
        )}
      </div>

        {/* Progress Circle */}
      {northStar.target_value && progress !== null && (
        <div className="flex flex-col items-center py-4">
          <div className="relative">
            <svg className="w-32 h-32 transform -rotate-90">
              {/* Background circle */}
              <circle
                cx="64"
                cy="64"
                r="56"
                stroke="#e5e7eb"
                strokeWidth="8"
                fill="none"
              />
              {/* Progress circle */}
              <circle
                cx="64"
                cy="64"
                r="56"
                stroke={progressColor}
                strokeWidth="8"
                fill="none"
                strokeDasharray={`${2 * Math.PI * 56}`}
                strokeDashoffset={`${2 * Math.PI * 56 * (1 - Math.min(progress, 100) / 100)}`}
                strokeLinecap="round"
                className="transition-all duration-500"
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-3xl font-black text-gray-900">
                {Math.round(Math.min(progress, 100))}%
              </span>
            </div>
          </div>

          {/* Current vs Target */}
          <div className="mt-4 text-center w-full">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                <div className="text-xs text-gray-500">Current</div>
                <div className="font-bold text-gray-900 mt-1">
                  {formatMetricValue(currentValue, northStar.metric_type)}
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                <div className="text-xs text-gray-500">Target</div>
                <div className="font-bold text-gray-900 mt-1">
                  {formatMetricValue(northStar.target_value, northStar.metric_type)}
                </div>
              </div>
            </div>

            {/* Gap to Goal */}
            {northStar.target_value && (
              <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                {achieved ? (
                  <div className="text-xs text-green-700 font-medium">
                    🎉 Goal achieved
                  </div>
                ) : (
                  <div className="text-xs text-blue-700 font-medium">
                    {formatMetricValue(gap ?? 0, northStar.metric_type)}{" "}
                    to go
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}


      {/* Time Remaining */}
      {showTimeRemaining && (
        <div className="mt-4 pt-4 border-t">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">Time remaining</span>
            <span
              className={`font-semibold ${
                daysRemaining! < 30
                  ? "text-red-600"
                  : daysRemaining! < 90
                  ? "text-yellow-600"
                  : "text-gray-900"
              }`}
            >
              {daysRemaining} days
            </span>
          </div>
          {daysRemaining! < 30 && (
            <div className="mt-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded p-2">
              ⚠️ Less than a month to reach your goal
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function getCurrentValue(northStar: NorthStar, currentMetrics: any): number {
  if (!northStar || !currentMetrics) return 0;
  switch (northStar.metric_type) {
    case "revenue":
      return Number(currentMetrics.revenue_usd ?? 0);
    case "active_subs":
      return Number(currentMetrics.active_subs ?? 0);
    case "orders":
      return Number(currentMetrics.orders ?? 0);
    case "customer_ltv":
      return Number(currentMetrics.customer_ltv ?? 0);
    default:
      return 0;
  }
}


function calculateProgress(northStar: NorthStar, currentMetrics: any): number | null {
  if (!northStar.target_value) return null;
  const current = getCurrentValue(northStar, currentMetrics);
  return (current / northStar.target_value) * 100;
}

function getDaysRemaining(deadline: string): number | null {
  try {
    const deadlineDate = new Date(deadline);
    const today = new Date();
    const diffTime = deadlineDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  } catch {
    return null;
  }
}

function getProgressColor(progress: number | null, daysRemaining: number | null): string {
  if (progress === null) return "#3b82f6";
  if (progress >= 100) return "#10b981";
  if (progress >= 80) return "#10b981";
  if (daysRemaining !== null && daysRemaining < 30) return "#ef4444";
  if (progress < 50) return "#f59e0b";
  return "#3b82f6";
}


function formatMetricValue(value: number | null | undefined, metricType?: string | null): string {
  if (value == null) return "—";
  if (metricType === "revenue" || metricType?.includes("ltv")) {
    return `$${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  }
  return value.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

function formatNSDate(dateStr: string): string {
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return dateStr;
  }
}

export default function Dashboard() {
  const { user, brands, logout } = useAuth();

  const searchParams = new URLSearchParams(window.location.search);
  const isDemoMode = searchParams.get('demo') === 'true';
  const DEMO_BRAND_ID = '1e1e0278-c588-4835-bc7c-bcfed09769f4';

  const [brandId, setBrandId] = useState<string | null>(null);

  const [rows, setRows] = useState<any[]>([]);
  const [subsRows, setSubsRows] = useState<any[]>([]);
  const [score, setScore] = useState<ScoreResp>(null);

  const [northStar, setNorthStar] = useState<NorthStar | null>(null);

  // Tasks: single fetch, client-side filtering
  const [allTasks, setAllTasks] = useState<Task[]>([]);
  const [taskTab, setTaskTab] = useState<"open" | "in_progress" | "completed">("open");

  const [kpiTable, setKpiTable] = useState<KpiRow[]>([]);
  const [loading, setLoading] = useState(false);

  const [showNewTask, setShowNewTask] = useState(false);

  const [showGlossary, setShowGlossary] = useState(false);

  const [showPwdModal, setShowPwdModal] = useState(false);

  const currentWeek = useMemo(
    () => mergeCurrentWeekMetrics(rows?.[0], subsRows?.[0]),
    [rows, subsRows]
  );



  const navigate = useNavigate();

  if (!user && !isDemoMode) {
    return (
      <div className="min-h-screen grid place-items-center bg-gradient-to-br from-gray-50 to-gray-100 p-6">
        <LoginForm redirectTo="/dashboard" />
      </div>
    );
  }

  useEffect(() => {
    
    if (isDemoMode) {
      setBrandId(DEMO_BRAND_ID);
      
      // Track demo view
      const demoEmail = sessionStorage.getItem('demoEmail');
      if (demoEmail) {
        fetch(`${API}/demo/track-view`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: demoEmail }),
        }).catch(() => {});
      }
    } else if (brands?.length) {
      setBrandId(brands[0].id);
    } else {
      console.log('No brands available yet');
    }
  }, [brands, isDemoMode, DEMO_BRAND_ID]);

  useEffect(() => {
    if (!brandId) {
      console.log('No brandId set, skipping data fetch');
      return;
    }
    
    let active = true;
    setLoading(true);
  
    // Choose endpoints based on demo mode
    const baseUrl = isDemoMode ? `${API}/demo` : `${API}/brands/${brandId}`;
    const fetchOptions = isDemoMode ? {} : { credentials: "include" as const };
  
    Promise.all([
      fetch(`${baseUrl}/metrics`, fetchOptions)
        .then((r) => {
          
          return r.json();
        })
        .then((data) => {
          
          return data;
        }),
      
      fetch(`${baseUrl}/subs-metrics`, fetchOptions)
        .then((r) => {
          
          return r.json();
        })
        .then((data) => {
          
          return data;
        })
        .catch(() => []),
      
      fetch(`${baseUrl}/runway-score`, fetchOptions)
        .then((r) => {
          
          return r.json();
        })
        .then((data) => {
          
          return data;
        })
        .catch(() => null),
      
      fetch(`${baseUrl}/tasks`, fetchOptions)
        .then((r) => {
          
          return r.json();
        })
        .then((data) => {
          
          return data;
        })
        .catch(() => []),
      
      fetch(
        isDemoMode ? `${API}/demo/kpis` : `${API}/brands/${brandId}/kpis/this-week/table`, 
        fetchOptions
      )
        .then((r) => {
          
          return r.json();
        })
        .then((data) => {
          
          return data;
        })
        .catch(() => []),

        fetch(`${baseUrl}/north-star`, fetchOptions)
        .then((r) => r.json())
        .then((data) => data)
        .catch(() => null),
    ])
      .then(([metrics, subsMetrics, scoreResp, taskList, kpis, northStarData]) => {
        if (!active) {
          
          return;
        }
        
        setRows(Array.isArray(metrics) ? metrics : []);
        setSubsRows(Array.isArray(subsMetrics) ? subsMetrics : []);
        setScore(scoreResp);
        setAllTasks(Array.isArray(taskList) ? taskList : []);
        setKpiTable(Array.isArray(kpis) ? kpis : []);
        setNorthStar(northStarData);
      })
      .catch((error) => {
        console.error('Error fetching data:', error);
      })
      .finally(() => {
        if (active) {
          
          setLoading(false);
        }
      });
  
    return () => {
      
      active = false;
    };
  }, [brandId, isDemoMode]); // IMPORTANT: Add isDemoMode to dependencies

  const runway = Math.round(score?.runway_score ?? 0);
  const ringColor = useMemo(() => {
    if (runway >= 80) return "ring-green-500 text-green-700 bg-green-50";
    if (runway >= 60) return "ring-yellow-500 text-yellow-700 bg-yellow-50";
    return "ring-red-500 text-red-700 bg-red-50";
  }, [runway]);

  const breakdown = useMemo(() => {
    const b = score?.breakdown || {};
    return Object.entries(b)
      .sort(([, a], [, c]) => (Math.abs((a as number) - 50) > Math.abs((c as number) - 50) ? -1 : 1))
      .slice(0, 4);
  }, [score]);

  const dueCount = score?.tasks_new_count ?? 0;
  const completedCount = score?.tasks_completed_count ?? 0;
  const overdueCount = score?.tasks_overdue_open_count ?? 0;
  const summaryText = score?.tasks_summary || "";
  const dueWeekList: { id: number; title: string; status?: string | null }[] =
    Array.isArray(score?.due_task_titles) ? (score?.due_task_titles as any) : [];

  const currentBrand = brands.find((b) => b.id === brandId);

  const priorityRank = { urgent: 3, high: 2, normal: 1, low: 0 } as const;
  const rank = (p?: Task["priority"]) => priorityRank[(p || "normal") as keyof typeof priorityRank] ?? 0;

  const compareTasks = (a: Task, b: Task) => {
    // 1) priority (desc)
    const pr = rank(b.priority) - rank(a.priority);
    if (pr !== 0) return pr;
  
    // 2) due_date (asc), nulls last — use lexical compare on YYYY-MM-DD
    const aDue = a.due_date ? a.due_date.slice(0, 10) : "9999-12-31";
    const bDue = b.due_date ? b.due_date.slice(0, 10) : "9999-12-31";
    const dueCmp = aDue.localeCompare(bDue);
    if (dueCmp !== 0) return dueCmp;
  
    // 3) created_at (desc) — fine to use Date here since time matters, not the calendar day
    const aCreated = a.created_at ? new Date(a.created_at).getTime() : 0;
    const bCreated = b.created_at ? new Date(b.created_at).getTime() : 0;
    return bCreated - aCreated;
  };  

  const sortTasks = useCallback((list: Task[]) => [...list].sort(compareTasks), []);


  // Derived task buckets + visible list
  const tasksOpen = useMemo(
    () => sortTasks(allTasks.filter((t) => (t.status || "").toLowerCase() === "open")),
    [allTasks, sortTasks]
  );
  const tasksInProgress = useMemo(
    () => sortTasks(allTasks.filter((t) => (t.status || "").toLowerCase() === "in_progress")),
    [allTasks, sortTasks]
  );
  const tasksCompleted = useMemo(
    () =>
      [...allTasks.filter((t) => (t.status || "").toLowerCase() === "completed")].sort(
        (a, b) =>
          new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime()
      ),
    [allTasks]
  );

  
  const visibleTasks =
    taskTab === "open" ? tasksOpen : taskTab === "in_progress" ? tasksInProgress : tasksCompleted;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center gap-4">
              <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
              
              {/* MODIFY THIS - Anonymize brand name in demo mode */}
              {currentBrand && (
                <div className="hidden sm:block text-sm text-gray-500 px-3 py-1 bg-gray-100 rounded-full">
                  {isDemoMode ? "Sample Brand" : currentBrand.name}
                </div>
              )}
              
              {/* ADD DEMO BADGE */}
              {isDemoMode && (
                <span className="text-xs font-semibold px-3 py-1.5 rounded-full bg-amber-100 text-amber-700 border border-amber-300">
                  🔍 DEMO MODE
                </span>
              )}
              
              {user?.role === "admin" && !isDemoMode && (
                <span className="text-xs font-semibold px-2 py-1 rounded bg-blue-100 text-blue-700">
                  ADMIN
                </span>
              )}
            </div>
            
            {/* MODIFY HEADER BUTTONS - Hide certain actions in demo mode */}
            <div className="flex items-center gap-3">
              {!isDemoMode && brands.length > 1 && (
                <select
                  className="border-2 border-gray-200 px-4 py-2 rounded-lg text-sm focus:border-blue-500 focus:outline-none transition-colors"
                  value={brandId || ""}
                  onChange={(e) => setBrandId(e.target.value)}
                >
                  {brands.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </select>
              )}
              
              {!isDemoMode && (
                <>
                  <button
                    onClick={() => setShowPwdModal(true)}
                    className="text-sm px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors font-medium border"
                  >
                    Change password
                  </button>
                  <button
                    onClick={async () => {
                      await logout();
                      navigate("/", { replace: true });
                    }}
                    className="text-sm px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors font-medium"
                  >
                    Log out
                  </button>
                </>
              )}
              
              {/* ADD EXIT DEMO BUTTON */}
              {isDemoMode && (
                <button
                  onClick={() => window.location.href = "/"}
                  className="text-sm px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-colors font-medium"
                >
                  Exit Demo
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      {isDemoMode && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-900">
            <strong>Demo Mode:</strong> You're viewing real metrics from an actual client dashboard. 
            Task details have been anonymized for privacy. Ready to see your own numbers? 
            <a href="/#cta" className="ml-1 underline font-semibold">Book a free call</a>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Runway Score + Tasks */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Runway Score Card */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 h-full">
            <h2 className="text-sm font-semibold text-gray-700 mb-4">
              Runway Score
              <HelpTip text={METRIC_DOCS.runway_score.desc} />
            </h2>
              <div className="flex flex-col items-center justify-center py-4">
                <div className={`inline-flex items-center justify-center w-32 h-32 rounded-full ring-8 ${ringColor}`}>
                  <span className="text-5xl font-black">{runway}</span>
                </div>
                <div className="mt-4 text-center">
                  <div className="text-sm font-medium text-gray-900">
                    {runway >= 80 ? "Excellent Health" : runway >= 60 ? "Fair Health" : "Needs Attention"}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">Updated weekly</div>
                  {/* Weekly task summary (from due_date-based view) */}
                  {(summaryText || dueCount || completedCount || overdueCount) && (
                    <div className="mt-6 w-full">
                      <div className="grid grid-cols-3 gap-2 text-center">
                        <div className="rounded-lg bg-gray-50 border border-gray-200 p-2">
                          <div className="text-xs text-gray-500">Due</div>
                          <div className="text-lg font-semibold">{dueCount}</div>
                        </div>
                        <div className="rounded-lg bg-gray-50 border border-gray-200 p-2">
                          <div className="text-xs text-gray-500">Completed</div>
                          <div className="text-lg font-semibold">{completedCount}</div>
                        </div>
                        <div className="rounded-lg bg-gray-50 border border-gray-200 p-2">
                          <div className="text-xs text-gray-500">Overdue</div>
                          <div className="text-lg font-semibold">{overdueCount}</div>
                        </div>
                      </div>

                      {summaryText && <div className="mt-3 text-sm text-gray-700">{summaryText}</div>}

                      {dueWeekList.length > 0 && (
                        <div className="mt-3">
                          <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">
                            Last week’s tasks
                          </div>
                          <ul className="space-y-1">
                            {dueWeekList.slice(0, 5).map((t) => (
                              <li
                                key={t.id}
                                className="flex items-center justify-between rounded-md bg-gray-50 border border-gray-200 px-3 py-2 text-sm"
                                title={t.title}
                              >
                                <span className="truncate">{t.title}</span>
                                {t.status && (
                                  <span
                                    className={
                                      "ml-2 rounded-full px-2 py-0.5 text-xs " +
                                      (t.status === "completed"
                                        ? "bg-green-100 text-green-700"
                                        : "bg-amber-100 text-amber-700")
                                    }
                                  >
                                    {t.status}
                                  </span>
                                )}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}

                </div>
              </div>

              {/* Key Drivers */}
              {breakdown.length > 0 && (
                <div className="mt-6 pt-6 border-t">
                  <div className="text-xs font-semibold text-gray-700 mb-3 uppercase tracking-wide">
                    Key Drivers
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {breakdown.map(([kpi, val]) => (
                      <div key={kpi} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                        <div className="text-xs text-gray-500">{labelForKPI(kpi)}</div>
                        <div className="text-lg font-bold text-gray-900 mt-1">
                          {Math.round(Number(val))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Tasks with Tabs */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 h-full">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-semibold text-gray-700">Tasks</h2>
                <div className="flex items-center gap-2">
                  {!isDemoMode && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setShowNewTask(true)}
                      className="px-3 py-2 text-sm rounded bg-blue-600 text-white hover:bg-blue-700"
                    >
                      Request Work
                    </button>
                  </div>
                )}
                </div>
              </div>

              {/* Tabs */}
              <div
                className="inline-flex rounded-lg border border-gray-200 overflow-hidden mb-4"
                role="tablist"
                aria-label="Task status"
              >
                <TabButton
                  active={taskTab === "open"}
                  onClick={() => setTaskTab("open")}
                  label={`Open (${tasksOpen.length})`}
                />
                <TabButton
                  active={taskTab === "in_progress"}
                  onClick={() => setTaskTab("in_progress")}
                  label={`In Progress (${tasksInProgress.length})`}
                />
                <TabButton
                  active={taskTab === "completed"}
                  onClick={() => setTaskTab("completed")}
                  label={`Completed (${tasksCompleted.length})`}
                />
              </div>

              <div className="space-y-2 max-h-96 overflow-y-auto" role="tabpanel" aria-live="polite">
              {loading ? (
                <div className="text-center py-8 text-gray-400">Loading tasks...</div>
              ) : visibleTasks.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <div className="text-4xl mb-2">✓</div>
                  <div>
                    No {taskTab === "open" ? "open" : taskTab === "in_progress" ? "in-progress" : "completed"} tasks
                  </div>
                </div>
              ) : (
                visibleTasks.map((t) => (
                  <div
                    key={t.id}
                    className="p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50/50 transition-all"
                  >
                    <div className="font-medium text-gray-900 mb-1">{t.title}</div>

                    {/* CHANGE THIS SECTION - Add demo mode check */}
                    {t.description && !isDemoMode && (
                      <Linkify text={t.description} />
                    )}
                    {t.description && isDemoMode && (
                      <div className="text-sm text-gray-400 italic blur-sm select-none pointer-events-none mb-2">
                        Task details are hidden in demo mode for client privacy
                      </div>
                    )}

                    <div className="flex flex-wrap items-center gap-2 text-xs text-gray-600">
                      {/* Status */}
                      <StatusPill status={humanizeStatus(t.status)} />

                      {/* Pending approval pill for brand-created tasks */}
                      {t.created_by_role === "brand" && (t.approved === false || t.status === "requested") && (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border bg-gray-100 text-gray-700 border-gray-200">
                          Pending approval
                        </span>
                      )}

                      {/* KPI */}
                      {t.kpi && (
                        <span className="px-2 py-0.5 rounded border bg-gray-50 border-gray-200">
                          {labelForKPI(t.kpi)}
                        </span>
                      )}

                      {/* Priority */}
                      {t.priority && (
                        <span className="px-2 py-0.5 rounded border bg-gray-50 border-gray-200 capitalize">
                          {t.priority}
                        </span>
                      )}

                      {/* Requested due date (for brand-created) */}
                      {t.requested_due_date && (
                        <span className="px-2 py-0.5 rounded border bg-gray-50 border-gray-200">
                          Requested {safeDate(t.requested_due_date)}
                        </span>
                      )}

                      {/* Due date */}
                      {t.due_date && (
                        <span className="px-2 py-0.5 rounded border bg-gray-50 border-gray-200">
                          Due {safeDate(t.due_date)}
                        </span>
                      )}

                      {/* Created date */}
                      {t.created_at && (
                        <span className="px-2 py-0.5 rounded border bg-gray-50 border-gray-200">
                          Created {safeDate(t.created_at)}
                        </span>
                      )}

                      {/* ID (handy for ops/debug) - HIDE IN DEMO MODE */}
                      {!isDemoMode && (
                        <span className="px-2 py-0.5 rounded border bg-gray-50 border-gray-200">ID: {t.id}</span>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
            </div>
          </div>
        </div>

        {/* North Star Card */}
        <div className="lg:col-span-1">
          <NorthStarCard northStar={northStar} currentMetrics={currentWeek} />
        </div>

        {/* KPI Table */}
        <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-gray-900">This Week's KPIs</h2>
          <div className="flex items-center gap-3 text-sm text-gray-500">
          {rows?.[0]?.week_start_date ? `Week of ${safeDate(rows[0].week_start_date)}` : ""}
            <button
              onClick={() => setShowGlossary(true)}
              className="ml-3 inline-flex items-center px-2 py-1 border rounded text-gray-700 hover:bg-gray-50"
            >
              Glossary
            </button>
          </div>
        </div>


          {loading ? (
            <div className="text-center py-8 text-gray-400">Loading KPIs...</div>
          ) : kpiTable.length === 0 ? (
            <div className="text-center py-8 text-gray-400">No KPIs for this week</div>
          ) : (
            <div className="space-y-8">
              {Object.entries(groupBy(kpiTable, (r) => r.kpi_group || "other"))
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([group, rows]) => (
                  <div key={group}>
                    <h3 className="text-xs uppercase tracking-wide font-semibold text-gray-500 mb-3 flex items-center gap-2">
                      {group === "orders" ? <>🛒 Orders</> : group === "subscriptions" ? <>📦 Subscriptions</> : <>{group}</>}
                    </h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b-2 border-gray-200">
                            <th className="text-left py-3 pr-4 font-semibold text-gray-700">KPI</th>
                            <th className="text-right py-3 pr-4 font-semibold text-gray-700">Current</th>
                            <th className="text-right py-3 pr-4 font-semibold text-gray-700">Target</th>
                            <th className="text-right py-3 pr-4 font-semibold text-gray-700">Gap</th>
                            <th className="text-left py-3 pr-4 font-semibold text-gray-700">Status</th>
                            {SHOW_SUGGESTED && (
                              <th className="text-left py-3 font-semibold text-gray-700">Suggested Action</th>
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((r, i) => (
                            <tr key={`${group}-${i}`} className="border-b border-gray-100 hover:bg-gray-50">
                              <td className="py-3 pr-4 font-medium text-gray-900">
                                <div className="inline-flex items-center">
                                  {r.kpi_label}
                                  <HelpTip text={docForRow(r).desc} />
                                </div>
                              </td>
                              <td className="py-3 pr-4 text-right font-semibold text-gray-900">
                                {fmtMaybeMoney(r.kpi_label, r.current_value)}
                              </td>
                              <td className="py-3 pr-4 text-right text-gray-600">
                                {fmtMaybeMoney(r.kpi_label, r.target_value)}
                              </td>
                              <td
                                className={`py-3 pr-4 text-right font-semibold ${
                                  Number(r.gap_value || 0) < 0 ? "text-red-600" : "text-green-600"
                                }`}
                              >
                                {fmtMaybeMoney(r.kpi_label, r.gap_value)}
                              </td>
                              <td className="py-3 pr-4">
                                <StatusPill status={r.status} />
                              </td>
                              {SHOW_SUGGESTED && (
                              <td className="py-3 text-gray-600 text-xs">
                                {r.suggested_lever ? (
                                  <span
                                    className={`px-2 py-1 rounded ${
                                      (r.status || "").toLowerCase() === "action"
                                        ? "bg-red-50 text-red-700 border border-red-200"
                                        : "bg-gray-50 text-gray-700 border border-gray-200"
                                    }`}
                                  >
                                    {r.suggested_lever}
                                  </span>
                                ) : "—"}
                              </td>
                            )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </section>

        {/* Weekly Metrics - Orders */}
        <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">🛒 Recent Weeks — Orders</h2>
          {loading ? (
            <div className="text-center py-8 text-gray-400">Loading...</div>
          ) : rows.length === 0 ? (
            <div className="text-center py-8 text-gray-400">No data available</div>
          ) : (
            <div className="space-y-3">
              {rows.map((r, idx) => {
                const prevWeek = rows[idx + 1];
                const cpcTrend =
                  prevWeek && prevWeek.contribution_per_customer_usd
                    ? ((r.contribution_per_customer_usd - prevWeek.contribution_per_customer_usd) /
                        prevWeek.contribution_per_customer_usd) *
                      100
                    : null;
                const salesTrend =
                  prevWeek && prevWeek.revenue_usd
                    ? ((r.revenue_usd - prevWeek.revenue_usd) / prevWeek.revenue_usd) * 100
                    : null;
                const prevConv = Number(prevWeek?.conversion_pct ?? 0);
                const currConv = Number(r.conversion_pct ?? 0);
                const convTrend =
                  prevWeek && isFinite(prevConv)
                    ? currConv - prevConv // absolute change in percentage points
                    : null;
                const ltvTrend =
                  prevWeek && prevWeek.ltv_contribution_usd
                    ? ((r.ltv_contribution_usd - prevWeek.ltv_contribution_usd) /
                        prevWeek.ltv_contribution_usd) *
                      100
                    : null;

                return (
                  <div
                    key={`${r.week_start_date}-orders`}
                    className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-sm transition-all"
                  >
                    <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                      <div>
                        <div className="text-xs text-gray-500 mb-1">Week</div>
                        <div className="font-semibold text-gray-900">{safeDate(r.week_start_date)}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 mb-1">CpC</div>
                        <div className="flex items-center gap-2">
                          <div className="font-semibold text-gray-900">${fmt(r.contribution_per_customer_usd)}</div>
                          {cpcTrend !== null && (
                            <span
                            className={`text-xs font-semibold ${
                              cpcTrend > 0
                                ? "text-green-600"
                                : cpcTrend < 0
                                ? "text-red-600"
                                : "text-gray-500"
                            }`}
                          >
                            {cpcTrend > 0
                              ? "↑"
                              : cpcTrend < 0
                              ? "↓"
                              : "→"}{" "}
                            {Math.abs(cpcTrend).toFixed(1)}%
                          </span>
                          )}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 mb-1">Net Sales</div>
                        <div className="flex items-center gap-2">
                          <div className="font-semibold text-gray-900">${fmt(r.revenue_usd)}</div>
                          {salesTrend !== null && (
                            <span
                            className={`text-xs font-semibold ${
                              salesTrend > 0
                                ? "text-green-600"
                                : salesTrend < 0
                                ? "text-red-600"
                                : "text-gray-500"
                            }`}
                          >
                            {salesTrend > 0
                              ? "↑"
                              : salesTrend < 0
                              ? "↓"
                              : "→"}{" "}
                            {Math.abs(salesTrend).toFixed(1)}%
                          </span>                          
                          )}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 mb-1">Conv Rate</div>
                        <div className="flex items-center gap-2">
                          <div className="font-semibold text-gray-900">{fmt(r.conversion_pct)}%</div>
                          {convTrend !== null && (
                            <span
                            className={`text-xs font-semibold ${
                              convTrend > 0
                                ? "text-green-600"
                                : convTrend < 0
                                ? "text-red-600"
                                : "text-gray-500"
                            }`}
                          >
                            {convTrend > 0
                              ? "↑"
                              : convTrend < 0
                              ? "↓"
                              : "→"}{" "}
                            {Math.abs(convTrend).toFixed(1)}%
                          </span>                          
                          )}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 mb-1">LTV</div>
                        <div className="flex items-center gap-2">
                          <div className="font-semibold text-gray-900">${fmt(r.ltv_contribution_usd)}</div>
                          {ltvTrend !== null && (
                            <span
                            className={`text-xs font-semibold ${
                              ltvTrend > 0
                                ? "text-green-600"
                                : ltvTrend < 0
                                ? "text-red-600"
                                : "text-gray-500"
                            }`}
                          >
                            {ltvTrend > 0
                              ? "↑"
                              : ltvTrend < 0
                              ? "↓"
                              : "→"}{" "}
                            {Math.abs(ltvTrend).toFixed(1)}%
                          </span>                          
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Weekly Metrics - Subscriptions */}
        <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">📦 Recent Weeks — Subscriptions</h2>
          {loading ? (
            <div className="text-center py-8 text-gray-400">Loading...</div>
          ) : subsRows.length === 0 ? (
            <div className="text-center py-8 text-gray-400">No subscription data available</div>
          ) : (
            <div className="space-y-3">
              {subsRows.map((r, idx) => {
                const prevWeek = subsRows[idx + 1];
                const cpoTrend =
                  prevWeek && prevWeek.contribution_per_order_usd
                    ? ((r.contribution_per_order_usd - prevWeek.contribution_per_order_usd) /
                        prevWeek.contribution_per_order_usd) *
                      100
                    : null;
                const subsTrend =
                  prevWeek && prevWeek.end_subs
                    ? ((r.end_subs - prevWeek.end_subs) / prevWeek.end_subs) * 100
                    : null;
                    const prevChurn = Number(prevWeek?.churn_pct ?? 0);
                    const currChurn = Number(r.churn_pct ?? 0);
                    const churnTrend =
                      prevWeek && isFinite(prevChurn)
                        ? currChurn - prevChurn // absolute change (e.g. 2.05 → 0.36 = -1.69)
                        : null;
                const ltvTrend =
                  prevWeek && prevWeek.subscriber_ltv_usd
                    ? ((r.subscriber_ltv_usd - prevWeek.subscriber_ltv_usd) / prevWeek.subscriber_ltv_usd) * 100
                    : null;

                return (
                  <div
                    key={`${r.week_start_date}-subs`}
                    className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-sm transition-all"
                  >
                    <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                      <div>
                        <div className="text-xs text-gray-500 mb-1">Week</div>
                        <div className="font-semibold text-gray-900">{safeDate(r.week_start_date)}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 mb-1">CpO</div>
                        <div className="flex items-center gap-2">
                          <div className="font-semibold text-gray-900">${fmt(r.contribution_per_order_usd)}</div>
                          {cpoTrend !== null && (
                            <span
                              className={`text-xs font-semibold ${
                                cpoTrend > 0
                                  ? "text-green-600"
                                  : cpoTrend < 0
                                  ? "text-red-600"
                                  : "text-gray-500"
                              }`}
                            >
                              {cpoTrend > 0
                                ? "↑"
                                : cpoTrend < 0
                                ? "↓"
                                : "→"}{" "}
                              {Math.abs(cpoTrend).toFixed(1)}%
                            </span>
                          )}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 mb-1">Active Subs</div>
                        <div className="flex items-center gap-2">
                          <div className="font-semibold text-gray-900">{fmt(r.end_subs)}</div>
                          {subsTrend !== null && (
                            <span
                            className={`text-xs font-semibold ${
                              subsTrend > 0
                                ? "text-green-600"
                                : subsTrend < 0
                                ? "text-red-600"
                                : "text-gray-500"
                            }`}
                          >
                            {subsTrend > 0
                              ? "↑"
                              : subsTrend < 0
                              ? "↓"
                              : "→"}{" "}
                            {Math.abs(subsTrend).toFixed(1)}%
                          </span>                          
                          )}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 mb-1">Churn</div>
                        <div className="flex items-center gap-2">
                          <div className="font-semibold text-gray-900">{fmt(r.churn_pct)}%</div>
                          {churnTrend !== null && (
                            <span
                            className={`text-xs font-semibold ${
                              churnTrend < 0
                                ? "text-green-600"
                                : churnTrend > 0
                                ? "text-red-600"
                                : "text-gray-500"
                            }`}
                          >
                            {churnTrend < 0
                              ? "↓"
                              : churnTrend > 0
                              ? "↑"
                              : "→"}{" "}
                            {Math.abs(churnTrend).toFixed(1)}%
                          </span>                          
                          )}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 mb-1">Sub LTV</div>
                        <div className="flex items-center gap-2">
                          <div className="font-semibold text-gray-900">${fmt(r.subscriber_ltv_usd)}</div>
                          {ltvTrend !== null && (
                            <span
                            className={`text-xs font-semibold ${
                              ltvTrend > 0
                                ? "text-green-600"
                                : ltvTrend < 0
                                ? "text-red-600"
                                : "text-gray-500"
                            }`}
                          >
                            {ltvTrend > 0
                              ? "↑"
                              : ltvTrend < 0
                              ? "↓"
                              : "→"}{" "}
                            {Math.abs(ltvTrend).toFixed(1)}%
                          </span>                          
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>

      {showNewTask && !isDemoMode && brandId && (
        <NewTaskModal
          brandId={brandId}
          onClose={() => setShowNewTask(false)}
          onCreated={(task) => setAllTasks((prev) => [task, ...prev])}
        />
      )}
      <GlossaryModal open={showGlossary} onClose={() => setShowGlossary(false)} />
          {showPwdModal && (
      <PasswordChangeModal
        onClose={() => setShowPwdModal(false)}
        onChanged={async () => {
          // optional: force a logout after change for security
          await logout();
          navigate("/", { replace: true });
        }}
      />
    )}
    </div>
  );
}

/* ---------- New Task Modal ---------- */
function NewTaskModal({
  brandId,
  onClose,
  onCreated,
}: {
  brandId: string;
  onClose: () => void;
  onCreated: (t: Task) => void;
}) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState<"low" | "normal" | "high" | "urgent">("normal");
  const [requestedDueDate, setRequestedDueDate] = useState<string>("");

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [submittedTask, setSubmittedTask] = useState<Task | null>(null);

  async function submit() {
    if (!title.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await fetch(`${API}/brands/${brandId}/tasks`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          title,
          description: description || null,
          // no KPI
          priority,
          requested_due_date: requestedDueDate || null,
        }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.error || "Unable to create task");
      }
      const data: Task = await res.json();
      onCreated(data);
      setSubmittedTask(data); // show success state
    } catch (e: any) {
      setErr(e.message || "Something went wrong");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="w-full max-w-lg bg-white rounded-xl p-6">
        {!submittedTask ? (
          <>
            <h3 className="text-lg font-semibold">Request Work</h3>
            {err && (
              <div className="mt-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">
                {err}
              </div>
            )}
            <div className="mt-4 space-y-3">
              <input
                className="w-full border rounded px-3 py-2"
                placeholder="Short title (e.g., “Fix checkout tracking”)"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={160}
              />
              <textarea
                className="w-full border rounded px-3 py-2 h-28"
                placeholder="Add helpful context…"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
              <span className="text-xs text-gray-600">Priority level</span>
              <select
                className="border rounded px-3 py-2 w-full"
                value={priority}
                onChange={(e) => setPriority(e.target.value as any)}
              >
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
              <label className="block">
                <span className="text-xs text-gray-600">Requested due date</span>
                <input
                  type="date"
                  className="mt-1 border rounded px-3 py-2 w-full"
                  value={requestedDueDate}
                  onChange={(e) => setRequestedDueDate(e.target.value)}
                />
              </label>
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button className="px-4 py-2 rounded border" onClick={onClose} disabled={busy}>
                Cancel
              </button>
              <button
                disabled={busy || !title.trim()}
                className="px-4 py-2 rounded bg-blue-600 text-white disabled:opacity-60"
                onClick={submit}
              >
                {busy ? "Submitting…" : "Submit"}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center gap-2 text-green-700 bg-green-50 border border-green-200 rounded p-3">
              <span className="text-lg">✓</span>
              <div className="text-sm">
                <div className="font-semibold">Request received — pending approval</div>
                <div className="text-green-800/75">
                  We’ll review your request and update its status soon.
                </div>
              </div>
            </div>

            <div className="mt-4 p-3 rounded border bg-gray-50">
              <div className="text-sm text-gray-600">Title</div>
              <div className="font-medium">{submittedTask.title}</div>
              {submittedTask.requested_due_date && (
                <div className="mt-2 text-sm text-gray-600">
                  Requested due date: <strong>{safeDate(submittedTask.requested_due_date)}</strong>
                </div>
              )}
              <div className="mt-2">
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border bg-gray-100 text-gray-700 border-gray-200">
                  Pending approval
                </span>
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button className="px-4 py-2 rounded bg-blue-600 text-white" onClick={onClose}>
                Done
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const METRIC_DOCS: Record<string, { label: string; desc: string; formula?: string }> = {
  revenue_usd: {
    label: "Net Sales ($)",
    desc: "Total revenue recognized for the week (after discounts/returns if your source does so).",
    formula: "Sum of order gross − discounts − refunds (per your data source)."
  },
  conversion_pct: {
    label: "Conversion Rate (%)",
    desc: "Percent of visits that result in an order.",
    formula: "orders / sessions × 100"
  },
  contribution_per_customer_usd: {
    label: "CpC ($)",
    desc: "Contribution per Customer: Average contribution margin after CAC per new customer order (weekly blended).",
    formula: "(gross margin per order − fulfillment per order) − CAC"
  },
  ltv_contribution_usd: {
    label: "Customer LTV ($)",
    desc: "Estimated lifetime contribution per customer (non-subscription).",
    formula: "contribution per order × (1 + repeat orders per customer)"
  },
  contribution_per_order_usd: {
    label: "CpO ($)",
    desc: "Contribution per Order: Average contribution margin per subscription order.",
    formula: "(subscription AOV × gross margin %) − fulfillment per order"
  },
  active_subs: {
    label: "Active Subs",
    desc: "Count of active subscribers at week end.",
    formula: "end_subs (snapshot)"
  },
  churn_pct: {
    label: "Churn (%)",
    desc: "Percent of subscribers who cancel in the week (lower is better).",
    formula: "cancels in week / (start_subs + new_subs) × 100"
  },
  subscriber_ltv_usd: {
    label: "Subscriber LTV ($)",
    desc: "Estimated lifetime value per subscriber on a contribution basis.",
    formula: "contribution per order × average active months"
  },
  runway_score: {
    label: "Runway Score",
    desc: "Composite health score (0–100) from your weighted KPIs.",
    formula: "weighted KPI sub-scores normalized to 100"
  }
};

/* ---------- Helpers ---------- */
// Inline “i” tooltip
function HelpTip({ text }: { text: string }) {
  const triggerRef = useRef<HTMLSpanElement | null>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  function updatePosition() {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const left = r.left + r.width / 2;
    const top = r.bottom + 8; // 8px gap
    setPos({
      // clamp a bit so it doesn't run off-screen
      left: Math.min(window.innerWidth - 12, Math.max(12, left)),
      top,
    });
  }


  useEffect(() => {
    if (!open) return;
    updatePosition();
    const onScroll = () => updatePosition();
    const onResize = () => updatePosition();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
  }, [open]);

  return (
    <>
      <span
        ref={triggerRef}
        className="ml-2 inline-flex items-center justify-center w-4 h-4 text-[10px] rounded-full border border-gray-300 text-gray-500 hover:bg-gray-100 cursor-default"
        aria-label="What is this?"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        tabIndex={0}
      >
        i
      </span>

      {open &&
        createPortal(
          <div
            role="tooltip"
            className="fixed z-[1000] px-2 py-1 text-xs bg-gray-800 text-white rounded shadow-lg pointer-events-none max-w-[260px]"
            style={{ top: pos.top, left: pos.left, transform: "translateX(-50%)" }}
          >
            {text}
          </div>,
          document.body
        )}
    </>
  );
}

// Glossary modal
function GlossaryModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="absolute inset-x-0 top-10 mx-auto w-full max-w-2xl bg-white rounded-xl shadow-xl border p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Metric Glossary</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">✕</button>
        </div>
        <div className="space-y-4 max-h-[70vh] overflow-y-auto">
          {Object.entries(METRIC_DOCS).map(([key, v]) => (
            <div key={key} className="border border-gray-200 rounded-lg p-3">
              <div className="font-medium text-gray-900">{v.label}</div>
              <div className="text-sm text-gray-700 mt-1">{v.desc}</div>
              {v.formula && (
                <div className="text-xs text-gray-500 mt-1">
                  <span className="font-semibold">Formula: </span>{v.formula}
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="mt-4 text-right">
          <button onClick={onClose} className="px-4 py-2 rounded bg-blue-600 text-white">Close</button>
        </div>
      </div>
    </div>
  );
}

function PasswordChangeModal({
  onClose,
  onChanged,
}: {
  onClose: () => void;
  onChanged: () => void; // called after a successful change (we log out by default)
}) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [ok, setOk] = useState(false);

  const canSubmit =
    !busy &&
    current.trim().length > 0 &&
    next.trim().length >= 8 &&
    next === confirm &&
    validatePassword(next) === null;

  async function submit() {
    setMsg(null);
    if (!canSubmit) return;
    setBusy(true);
    try {
      const res = await fetch(`${API}/auth/change-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          current_password: current,
          new_password: next,
        }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.error || "Could not change password");
      }
      setOk(true);
    } catch (e: any) {
      setMsg(e.message || "Something went wrong");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-[60]">
      <div className="w-full max-w-md bg-white rounded-xl p-6 border shadow-lg">
        {!ok ? (
          <>
            <h3 className="text-lg font-semibold">Change password</h3>
            {msg && (
              <div className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">
                {msg}
              </div>
            )}
            <div className="mt-4 space-y-3">
              <label className="block">
                <span className="text-sm text-gray-700">Current password</span>
                <input
                  type="password"
                  className="mt-1 w-full border rounded px-3 py-2"
                  value={current}
                  onChange={(e) => setCurrent(e.target.value)}
                  autoComplete="current-password"
                />
              </label>

              <label className="block">
                <span className="text-sm text-gray-700">New password</span>
                <input
                  type="password"
                  className="mt-1 w-full border rounded px-3 py-2"
                  value={next}
                  onChange={(e) => setNext(e.target.value)}
                  autoComplete="new-password"
                />
                <PasswordHint value={next} />
              </label>

              <label className="block">
                <span className="text-sm text-gray-700">Confirm new password</span>
                <input
                  type="password"
                  className="mt-1 w-full border rounded px-3 py-2"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  autoComplete="new-password"
                />
                {confirm && confirm !== next && (
                  <div className="text-xs text-red-600 mt-1">Passwords don’t match.</div>
                )}
              </label>
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button className="px-4 py-2 rounded border" onClick={onClose} disabled={busy}>
                Cancel
              </button>
              <button
                className="px-4 py-2 rounded bg-blue-600 text-white disabled:opacity-50"
                disabled={!canSubmit}
                onClick={submit}
              >
                {busy ? "Saving…" : "Change password"}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="text-green-700 bg-green-50 border border-green-200 rounded p-3">
              Password updated successfully.
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button className="px-4 py-2 rounded border" onClick={onClose}>
                Stay signed in
              </button>
              <button className="px-4 py-2 rounded bg-blue-600 text-white" onClick={onChanged}>
                Log out now
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function validatePassword(pw: string): string | null {
  if (pw.length < 8) return "Use at least 8 characters.";
  if (!/[A-Za-z]/.test(pw) || !/[0-9]/.test(pw)) return "Use letters and at least one number.";
  // Optional: add special char / uppercase rules here.
  return null;
}

function PasswordHint({ value }: { value: string }) {
  const err = validatePassword(value);
  if (!value) return null;
  return (
    <div className={`text-xs mt-1 ${err ? "text-red-600" : "text-green-700"}`}>
      {err ?? "Strong enough."}
    </div>
  );
}



function groupBy<T>(arr: T[], keyFn: (x: T) => string) {
  return arr.reduce<Record<string, T[]>>((acc, item) => {
    const k = keyFn(item);
    (acc[k] ||= []).push(item);
    return acc;
  }, {});
}

function fmt(n: any) {
  const v = Number(n ?? 0);
  return v.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function isMoneyLabel(label: string) {
  return /\(\$\)/i.test(label);
}

function isPercentLabel(label: string) {
  return /\(%\)/i.test(label);
}

function fmtMaybeMoney(label: string, val: number | null | undefined) {
  if (val == null || Number.isNaN(Number(val))) return "—";
  const v = Number(val);
  if (isMoneyLabel(label)) {
    return `$${v.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  }
  if (isPercentLabel(label)) {
    return `${v.toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;
  }
  return v.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function Linkify({ text, className = "" }: { text: string; className?: string }) {
  const urlRe = /(https?:\/\/[^\s<>()\[\]{}"']+|www\.[^\s<>()\[\]{}"']+)/gi;
  const parts: Array<string | JSX.Element> = [];
  let lastIndex = 0, m: RegExpExecArray | null;

  while ((m = urlRe.exec(text))) {
    const start = m.index, end = urlRe.lastIndex;
    if (start > lastIndex) parts.push(text.slice(lastIndex, start));

    let url = m[0];
    const trimmed = url.replace(/[),.;!?]+$/g, "");
    const trailing = url.slice(trimmed.length);
    url = trimmed;

    const href = url.startsWith("http") ? url : `https://${url}`;

    parts.push(
      <a
        key={`${start}-${end}`}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 underline break-all"
      >
        {shorten(url)}
      </a>
    );
    if (trailing) parts.push(trailing);
    lastIndex = end;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));

  // NOTE: break-words allows soft wrap anywhere; overflow-x-hidden prevents horizontal scroll
  return (
    <p className={`text-sm text-gray-700 mb-2 whitespace-pre-wrap break-words overflow-x-hidden ${className}`}>
      {parts}
    </p>
  );
}

function shorten(url: string, max = 48) {
  return url.length <= max ? url : `${url.slice(0, 28)}…${url.slice(-12)}`;
}


function StatusPill({ status }: { status: string }) {
  const s = (status || "").toLowerCase();
  const style =
    s === "ok"
      ? "bg-green-100 text-green-700 border-green-200"
      : s === "watch"
      ? "bg-yellow-100 text-yellow-700 border-yellow-200"
      : s === "action"
      ? "bg-red-100 text-red-700 border-red-200"
      : "bg-gray-100 text-gray-700 border-gray-200";
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${style}`}>
      {status}
    </span>
  );
}

function safeDate(d: any) {
  if (!d) return "";
  try {
    // If the string starts with YYYY-MM-DD, trust those 10 chars literally
    if (typeof d === "string") {
      const m = d.match(/^(\d{4}-\d{2}-\d{2})/);
      if (m) {
        const [y, mo, da] = m[1].split("-");
        return `${+mo}/${+da}/${y}`;
      }
    }
    // Fallback: format with UTC parts to avoid local TZ shifts
    const dt = d instanceof Date ? d : new Date(d);
    if (Number.isNaN(dt.getTime())) return String(d ?? "");
    return `${dt.getUTCMonth() + 1}/${dt.getUTCDate()}/${dt.getUTCFullYear()}`;
  } catch {
    return String(d ?? "");
  }
}



function labelForKPI(k: string) {
  const map: Record<string, string> = {
    net_sales: "Net Sales",
    conversion_rate: "Conversion Rate",
    cpc: "CpC",
    cpo: "CpO",
    churn: "Churn",
    active_subs: "Active Subs",
    subscriber_ltv: "Subscriber LTV",
    customer_ltv: "Customer LTV",
  };
  return map[k] || k;
}

function docForRow(r: { kpi_label: string; kpi_key?: string }) {
  // Prefer a machine key if your API provides it
  const byKey = r && (METRIC_DOCS as any)[(r as any).kpi_key || ""];
  if (byKey) return byKey;

  // Fallback: resolve by label text
  const label = (r?.kpi_label || "").toLowerCase();
  if (label.includes("net sales")) return METRIC_DOCS.revenue_usd;
  if (label.includes("conversion")) return METRIC_DOCS.conversion_pct;
  if (label.includes("cpc")) return METRIC_DOCS.contribution_per_customer_usd;
  if (label.includes("customer ltv")) return METRIC_DOCS.ltv_contribution_usd;
  if (label.includes("cpo")) return METRIC_DOCS.contribution_per_order_usd;
  if (label.includes("active subs")) return METRIC_DOCS.active_subs;
  if (label.includes("churn")) return METRIC_DOCS.churn_pct;
  if (label.includes("subscriber ltv")) return METRIC_DOCS.subscriber_ltv_usd;
  return { label: r.kpi_label, desc: "No description available." };
}


function TabButton({ active, onClick, label }: { active: boolean; onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      role="tab"
      aria-selected={active}
      className={`px-3 sm:px-4 py-2 text-sm font-medium transition-colors ${
        active ? "bg-blue-600 text-white" : "bg-white hover:bg-gray-50 text-gray-700"
      } ${active ? "" : "border-l border-gray-200"}`}
    >
      {label}
    </button>
  );
}

function humanizeStatus(s: string) {
  const v = (s || "").toLowerCase();
  if (v === "in_progress") return "In Progress";
  if (v === "completed") return "Completed";
  if (v === "open") return "Open";
  if (v === "requested") return "Requested";
  return s || "—";
}

  // helper (put near other helpers)
  function mergeCurrentWeekMetrics(ordersRow?: any, subsRow?: any) {
    const o = ordersRow || {};
    const s = subsRow || {};
  
    return {
      // keep originals
      ...o,
      ...s,
  
      // normalized fields the North Star card will use
      revenue_usd: Number(o.revenue_usd ?? 0),
      orders:
        Number(o.orders ?? o.total_orders ?? o.order_count ?? 0),
  
      active_subs:
        Number(s.active_subs ?? s.end_subs ?? 0),
  
      customer_ltv:
        Number(
          // non-subscription (customer) LTV from orders side
          o.ltv_contribution_usd ??
          // (optional) fallback if you ever want to use sub LTV instead
          // s.subscriber_ltv_usd ??
          0
        ),
    };
  }